import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUser } from "../contexts/UserContext";
import { getDodoCheckoutUrl } from "./firebaseFunctions";
import styles from "./Pricing.module.css";
import productCatalog from "../productCatalog.json";

type SubscriptionPlan = {
  id: string;
  labelKey: string;
  defaultLabel: string;
  descriptionKey: string;
  defaultDescription: string;
  imageSrc: string;
  creditsPerMonth: number;
  price: string;
  billingPeriodKey: string;
  highlight?: boolean;
};

type CreditPack = {
  id: string;
  labelKey: string;
  descriptionKey: string;
  imageSrc: string;
  credits: number;
  price: string;
};

const CHECKOUT_SYNC_STORAGE_KEY = "autovid_dodo_checkout_success";
const SUBSCRIPTION_PRODUCT_IDS = new Set<string>([
  "creator_300_monthly",
  "creator_500_monthly",
  "creator_1000_monthly",
]);
const SUCCESS_RETURN_STATUSES = new Set<string>([
  "succeeded",
  "success",
  "paid",
  "active",
]);

const Pricing: React.FC = () => {
  const { t } = useTranslation();
  const userContext = useUser();
  const userDetails = userContext?.userDetails;
  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const currentLang = lang ?? "en";
  const [pendingProductId, setPendingProductId] = useState<string | null>(null);
  const hasActiveSubscription = userDetails?.hasSubscribed === true;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = (params.get("status") ?? "").trim().toLowerCase();
    const paymentId = (params.get("payment_id") ?? "").trim();
    const subscriptionId = (params.get("subscription_id") ?? "").trim();
    const hasCheckoutReturnId = paymentId.length > 0 || subscriptionId.length > 0;

    if (!hasCheckoutReturnId || !SUCCESS_RETURN_STATUSES.has(status)) {
      return;
    }

    const payload = {
      status,
      paymentId,
      subscriptionId,
      at: Date.now(),
    };

    try {
      localStorage.setItem(CHECKOUT_SYNC_STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // ignore localStorage write failures
    }

    if (window.opener && !window.opener.closed) {
      try {
        window.opener.postMessage(
          { type: "AUTOVID_DODO_CHECKOUT_SUCCESS", payload },
          window.location.origin,
        );
      } catch {
        // ignore cross-window messaging failures
      }
    }

    window.history.replaceState({}, document.title, window.location.pathname);

    if (window.opener && !window.opener.closed) {
      try {
        window.close();
      } catch {
        // ignore window close failures
      }
    }
  }, []);

  useEffect(() => {
    const reloadFromCheckout = () => {
      window.location.reload();
    };

    const onStorage = (event: StorageEvent) => {
      if (event.key !== CHECKOUT_SYNC_STORAGE_KEY || !event.newValue) {
        return;
      }
      reloadFromCheckout();
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }
      if (event.data?.type !== "AUTOVID_DODO_CHECKOUT_SUCCESS") {
        return;
      }
      reloadFromCheckout();
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener("message", onMessage);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("message", onMessage);
    };
  }, []);

  const subscriptions: SubscriptionPlan[] = [
    {
      ...productCatalog.subscriptions[0],
      labelKey: "pricing_page.subscriptions.lite_300.label",
      defaultLabel: "Lite",
      descriptionKey: "pricing_page.subscriptions.lite_300.description",
      defaultDescription:
        "For creators getting started\nEstimated output: about 30-42 Shorts (1 min) or 15-30 Long-form videos (5 min)",
      imageSrc: "/assets/images/AUTOVID_LITE.png",
      creditsPerMonth: productCatalog.subscriptions[0].credits,
      billingPeriodKey: "pricing_page.per_month",
    },
    {
      ...productCatalog.subscriptions[1],
      labelKey: "pricing_page.subscriptions.standard_500.label",
      defaultLabel: "Standard",
      descriptionKey: "pricing_page.subscriptions.standard_500.description",
      defaultDescription:
        "Most balanced default plan\nEstimated output: about 50-71 Shorts (1 min) or 25-50 Long-form videos (5 min)",
      imageSrc: "/assets/images/AUTOVID_STANDARD.png",
      creditsPerMonth: productCatalog.subscriptions[1].credits,
      billingPeriodKey: "pricing_page.per_month",
      highlight: productCatalog.subscriptions[1].highlight,
    },
    {
      ...productCatalog.subscriptions[2],
      labelKey: "pricing_page.subscriptions.pro_1000.label",
      defaultLabel: "Pro",
      descriptionKey: "pricing_page.subscriptions.pro_1000.description",
      defaultDescription:
        "For channels with frequent uploads\nEstimated output: about 100-142 Shorts (1 min) or 50-100 Long-form videos (5 min)",
      imageSrc: "/assets/images/AUTOVID_PRO.png",
      creditsPerMonth: productCatalog.subscriptions[2].credits,
      billingPeriodKey: "pricing_page.per_month",
    },
  ];

  const creditPacks: CreditPack[] = [
    {
      ...productCatalog.creditPacks[0],
      labelKey: "pricing_page.packs.creator_500.label",
      descriptionKey: "pricing_page.packs.creator_500.description",
      imageSrc: "/assets/images/CRD_500.png",
    },
    {
      ...productCatalog.creditPacks[1],
      labelKey: "pricing_page.packs.pro_1000.label",
      descriptionKey: "pricing_page.packs.pro_1000.description",
      imageSrc: "/assets/images/CRD_1000.png",
    },
  ];

  const startCheckout = async (productId: string) => {
    const userEmail = userDetails?.id ?? "";
    if (userEmail.length === 0 || userEmail === "Guest") {
      alert(
        t("checkout_select.login_required", {
          defaultValue: "Please log in before starting payment.",
        }),
      );
      return;
    }

    if (hasActiveSubscription && SUBSCRIPTION_PRODUCT_IDS.has(productId)) {
      alert(
        t("checkout_select.already_subscribed", {
          defaultValue:
            currentLang.toLowerCase().startsWith("ko")
              ? "이미 활성 구독이 있습니다. 다른 구독을 구매하려면 먼저 기존 구독을 해지해 주세요."
              : "You already have an active subscription. Cancel it first to buy another subscription.",
        }),
      );
      return;
    }

    try {
      setPendingProductId(productId);
      const checkoutUrl = await getDodoCheckoutUrl(
        userEmail,
        productId,
        currentLang,
      );
      // Move in the current tab so checkout returns to the same app context.
      window.location.assign(checkoutUrl);
    } catch (error: any) {
      alert(
        error?.message ??
          t("checkout_select.not_ready", {
            defaultValue: "Checkout is currently unavailable.",
          }),
      );
    } finally {
      setPendingProductId(null);
    }
  };

  const handleEnterpriseContact = () => {
    navigate(`/${currentLang}/support?type=enterprise`);
  };

  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <h1>{t("pricing_page.hero.title")}</h1>
        <p className={styles.heroSubtitle}>{t("pricing_page.hero.subtitle")}</p>
      </section>

      <section
        className={`${styles.section} ${styles.costAdvantageSection}`}
        aria-labelledby="pricing-cost-advantage-title"
      >
        <div className={styles.costAdvantagePanel}>
          <div className={styles.costAdvantageCopy}>
            <span className={styles.costAdvantageEyebrow}>
              {t("pricing_page.cost_advantage.eyebrow", {
                defaultValue: "Cost advantage",
              })}
            </span>
            <h2 id="pricing-cost-advantage-title">
              {t("pricing_page.cost_advantage.title", {
                defaultValue:
                  "1-minute Shorts about $0.30,\n5-minute long-form about $0.30-$0.60",
              })}
            </h2>
            <p>
              {t("pricing_page.cost_advantage.length_note", {
                defaultValue:
                  "Shorts can be up to 3 minutes, and long-form videos can be up to 1 hour.",
              })}
            </p>
            <p>
              {t("pricing_page.cost_advantage.body", {
                defaultValue:
                  "AUTOVID keeps production costs low by automating the parts creators repeat most: images, voice, subtitles, editing, and publishing.",
              })}
            </p>
            <p>
              {t("pricing_page.cost_advantage.comparison", {
                defaultValue:
                  "Compared with full generative AI video tools that can cost several dollars per minute, AUTOVID is built for affordable, repeatable content production.",
              })}
            </p>
          </div>

          <div
            className={styles.costStats}
            aria-label={t("pricing_page.cost_advantage.stats_label", {
              defaultValue: "AUTOVID estimated production cost highlights",
            })}
          >
            <div className={styles.costStat}>
              <strong>
                {t("pricing_page.cost_advantage.stats.per_minute.value", {
                  defaultValue: "About $0.30",
                })}
              </strong>
              <span>
                {t("pricing_page.cost_advantage.stats.per_minute.label", {
                  defaultValue: "Estimated per 1-minute Short",
                })}
              </span>
            </div>
            <div className={styles.costStat}>
              <strong>
                {t("pricing_page.cost_advantage.stats.longform_cost.value", {
                  defaultValue: "About $0.30-$0.60",
                })}
              </strong>
              <span>
                {t("pricing_page.cost_advantage.stats.longform_cost.label", {
                  defaultValue: "Estimated per 5-minute long-form video",
                })}
              </span>
            </div>
            <div className={styles.costStat}>
              <strong>
                {t("pricing_page.cost_advantage.stats.pro_floor.value", {
                  defaultValue: "10 CRD",
                })}
              </strong>
              <span>
                {t("pricing_page.cost_advantage.stats.pro_floor.label", {
                  defaultValue: "Typical 1-minute Short usage",
                })}
              </span>
            </div>
            <div className={styles.costStat}>
              <strong>
                {t("pricing_page.cost_advantage.stats.cost_gap.value", {
                  defaultValue: "10x+",
                })}
              </strong>
              <span>
                {t("pricing_page.cost_advantage.stats.cost_gap.label", {
                  defaultValue:
                    "Potential cost gap versus many full AI video workflows",
                })}
              </span>
            </div>
          </div>
        </div>

        <p className={`${styles.note} ${styles.costAdvantageNote}`}>
          {t("pricing_page.cost_advantage.note", {
            defaultValue:
              "The $0.30 Short estimate assumes about 10 CRD for a 1-minute Short. The $0.30-$0.60 long-form estimate assumes about 10-20 CRD for a 5-minute video. Actual cost may be higher depending on plan, model settings, extra features, regeneration count, and exchange rates. Competitor pricing is based on publicly listed credit pricing as of May 2026.",
          })}
        </p>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2>{t("pricing_page.subscriptions.title")}</h2>
          <p className={styles.sectionSubtitle}>
            {t("pricing_page.subscriptions.subtitle", {
              defaultValue:
                "Choose the monthly plan that matches your upload volume.",
            })}
          </p>
          {hasActiveSubscription && (
            <p className={styles.smallPrint}>
              {t("checkout_select.already_subscribed", {
                defaultValue:
                  currentLang.toLowerCase().startsWith("ko")
                    ? "이미 활성 구독이 있습니다. 다른 구독을 구매하려면 먼저 기존 구독을 해지해 주세요."
                    : "You already have an active subscription. Cancel it first to buy another subscription.",
              })}
            </p>
          )}
        </div>

        <div className={`${styles.cardGrid} ${styles.subscriptionGrid}`}>
          {subscriptions.map((plan) => (
            <article
              key={plan.id}
              className={`${styles.card} ${
                plan.highlight ? styles.cardHighlight : ""
              }`}
            >
              {plan.highlight && (
                <div className={styles.badge}>
                  {t("pricing_page.subscriptions.recommended")}
                </div>
              )}

              <div
                className={`${styles.cardImageWrap} ${styles.subscriptionImageWrap}`}
              >
                <img
                  src={plan.imageSrc}
                  alt={t(plan.labelKey, { defaultValue: plan.defaultLabel })}
                  className={styles.cardImage}
                  loading="lazy"
                />
              </div>

              <h3 className={styles.cardTitle}>
                {t(plan.labelKey, { defaultValue: plan.defaultLabel })}
              </h3>
              <p className={styles.cardDescription}>
                {t(plan.descriptionKey, {
                  defaultValue: plan.defaultDescription,
                })}
              </p>

              <div className={styles.priceBlock}>
                <span className={styles.price}>{plan.price}</span>
                <span className={styles.billingPeriod}>
                  {t(plan.billingPeriodKey)}
                </span>
              </div>

              <div className={styles.creditsInfo}>
                {t("pricing_page.subscriptions.credits_per_month", {
                  count: plan.creditsPerMonth,
                })}
              </div>

              <button
                type="button"
                className={styles.primaryButton}
                disabled={
                  pendingProductId !== null ||
                  (hasActiveSubscription &&
                    SUBSCRIPTION_PRODUCT_IDS.has(plan.id))
                }
                onClick={() => startCheckout(plan.id)}
              >
                {hasActiveSubscription && SUBSCRIPTION_PRODUCT_IDS.has(plan.id)
                  ? t("pricing_page.already_subscribed", {
                      defaultValue: "Already Subscribed",
                    })
                  : pendingProductId === plan.id
                    ? t("pricing_page.redirecting", {
                        defaultValue: "Redirecting...",
                      })
                    : t("pricing_page.buy_now", { defaultValue: "Buy Now" })}
              </button>
            </article>
          ))}
        </div>

        <p className={styles.note}>
          {t("pricing_page.subscriptions.usage_note", {
            defaultValue:
              "Estimated output is based on average usage and may vary depending on voice/image model, effect settings, and regeneration count.",
          })}
        </p>
        <p className={`${styles.note} ${styles.noteSecondary}`}>
          {t("pricing_page.subscriptions.credit_expiry_note", {
            defaultValue:
              currentLang.toLowerCase().startsWith("ko")
                ? "구독으로 지급된 CRD는 30일간 유효하며, 유효기간 경과 후 7일 유예기간 뒤 소멸됩니다."
                : "Subscription credits are valid for 30 days, then remain in a 7-day grace period before expiration.",
          })}
        </p>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2>{t("pricing_page.packs.title")}</h2>
          <p className={styles.sectionSubtitle}>
            {t("pricing_page.packs.subtitle")}
          </p>
        </div>

        <div className={styles.cardGrid}>
          {creditPacks.map((pack) => (
            <article key={pack.id} className={styles.card}>
              <div
                className={`${styles.cardImageWrap} ${styles.packImageWrap}`}
              >
                <img
                  src={pack.imageSrc}
                  alt={t(pack.labelKey)}
                  className={styles.cardImage}
                  loading="lazy"
                />
              </div>
              <h3 className={styles.cardTitle}>{t(pack.labelKey)}</h3>
              <p className={styles.cardDescription}>{t(pack.descriptionKey)}</p>

              <div className={styles.priceBlock}>
                <span className={styles.price}>{pack.price}</span>
              </div>

              <div className={styles.creditsInfo}>
                {t("pricing_page.packs.credits_total", {
                  count: pack.credits,
                })}
              </div>

              <button
                type="button"
                className={styles.secondaryButton}
                disabled={pendingProductId !== null}
                onClick={() => startCheckout(pack.id)}
              >
                {pendingProductId === pack.id
                  ? t("pricing_page.redirecting", {
                      defaultValue: "Redirecting...",
                    })
                  : t("pricing_page.buy_now", { defaultValue: "Buy Now" })}
              </button>
            </article>
          ))}
        </div>

        <p className={styles.smallPrint}>{t("pricing_page.small_print")}</p>
        <p className={styles.smallPrint}>
          {t("pricing_page.refund_policy_note", {
            defaultValue: currentLang.toLowerCase().startsWith("ko")
              ? "결제 전 환불 기준을 확인해 주세요."
              : "Please review the refund policy before payment.",
          })}{" "}
          <Link
            className={styles.inlinePolicyLink}
            to={`/${currentLang}/refund-policy`}
          >
            {t("footer.refund_policy", {
              defaultValue: currentLang.toLowerCase().startsWith("ko")
                ? "환불정책"
                : "Refund Policy",
            })}
          </Link>
        </p>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2>{t("pricing_page.enterprise.title")}</h2>
          <p className={styles.sectionSubtitle}>
            {t("pricing_page.enterprise.subtitle")}
          </p>
        </div>

        <div className={styles.cardGridSingle}>
          <article className={`${styles.card} ${styles.cardHighlight}`}>
            <div className={styles.badge}>
              {t("pricing_page.enterprise.badge")}
            </div>

            <h3 className={styles.cardTitle}>
              {t("pricing_page.enterprise.plan_name")}
            </h3>
            <p className={styles.cardDescription}>
              {t("pricing_page.enterprise.description")}
            </p>

            <div className={styles.priceBlock}>
              <span className={styles.price}>
                {t("pricing_page.enterprise.custom_pricing")}
              </span>
            </div>

            <div className={styles.creditsInfo}>
              {t("pricing_page.enterprise.features_summary")}
            </div>

            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleEnterpriseContact}
            >
              {t("pricing_page.enterprise.cta")}
            </button>
          </article>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
